import type { SpacingToken } from '../../components/ui/box-classes';

import type {
  DataTableColumnMeta,
  DataTableDensity,
} from './data-table-types';
import type { Omittable } from "../../lib/props";
import { ROW_RUNGS } from "../../lib/row-rungs";

/**
 * Density configuration for cell padding and text styling.
 */
export const DENSITY_CONFIG: Record<
  DataTableDensity,
  {
    cellPadding: string;
    cellPaddingWithLeft: string;
    headerPadding: string;
    textSize: string;
    rowHeight: number;
    /** Column-header row height. The text-sm and text-base rungs seat the
     *  header on the ROW_RUNGS.tableHeader band; compact keeps a 32px band
     *  over its 32px rows. */
    headerHeight: number;
    /** Pixel value of cellPadding's `pl-*` — leading-rail alignment math. */
    cellPadLeftPx: number;
    /** Edge-cell padding per `paddingX` token, with this density's own
     *  `cellPadding` inset subtracted — so first/last column CONTENT lands
     *  exactly `paddingX` from the table edge (clamped at zero when the
     *  internal inset already exceeds the token). Tailwind's scanner needs
     *  literal class names, hence enumerated maps instead of arithmetic. */
    edgeGutterLeft: Record<SpacingToken, string>;
    edgeGutterRight: Record<SpacingToken, string>;
  }
> = {
  compact: {
    cellPadding: 'pl-3 pr-1 py-0.5',
    cellPaddingWithLeft: 'pl-1 pr-2 py-0.5',
    headerPadding: 'pl-3 pr-1 py-1',
    textSize: 'text-xs',
    rowHeight: 32,
    headerHeight: 32,
    cellPadLeftPx: 12,
    // Internal inset pl-3 (12px) / pr-1 (4px).
    edgeGutterLeft: { none: '', '2xs': '', xs: '', sm: '', base: '', md: 'pl-1', lg: 'pl-3', xl: 'pl-5', '2xl': 'pl-9' },
    edgeGutterRight: { none: '', '2xs': '', xs: '', sm: 'pr-1', base: 'pr-2', md: 'pr-3', lg: 'pr-5', xl: 'pr-7', '2xl': 'pr-11' },
  },
  // The default rung: a single text-sm line in a ROW_RUNGS.tableRow row (44,
  // the touch-target minimum) with the cell ring (rowHeight − 4) around it.
  // The header band is ROW_RUNGS.tableHeader so the column labels sit on the
  // rows' rhythm; both are set in lib/row-rungs.ts, nowhere else.
  regular: {
    cellPadding: 'pl-3 pr-1 py-1',
    cellPaddingWithLeft: 'pl-1 pr-2 py-1',
    headerPadding: 'pl-3 pr-1 py-1',
    textSize: 'text-sm',
    rowHeight: ROW_RUNGS.tableRow,
    headerHeight: ROW_RUNGS.tableHeader,
    cellPadLeftPx: 12,
    // Internal inset pl-3 (12px) / pr-1 (4px).
    edgeGutterLeft: { none: '', '2xs': '', xs: '', sm: '', base: '', md: 'pl-1', lg: 'pl-3', xl: 'pl-5', '2xl': 'pl-9' },
    edgeGutterRight: { none: '', '2xs': '', xs: '', sm: 'pr-1', base: 'pr-2', md: 'pr-3', lg: 'pr-5', xl: 'pr-7', '2xl': 'pr-11' },
  },
  // `regular` in every respect but the row: 52px so a stacked two-line
  // identity cell (14px label over a 12px second line) sits with real air
  // above and below it. The cell ring grows with the row (it stays 2px off
  // the hairlines, like every rung), so the editors inside it get the same
  // air the stacked cell got.
  comfortable: {
    cellPadding: 'pl-3 pr-1 py-1',
    cellPaddingWithLeft: 'pl-1 pr-2 py-1',
    headerPadding: 'pl-3 pr-1 py-1',
    textSize: 'text-sm',
    rowHeight: 52,
    headerHeight: ROW_RUNGS.tableHeader,
    cellPadLeftPx: 12,
    // Internal inset pl-3 (12px) / pr-1 (4px).
    edgeGutterLeft: { none: '', '2xs': '', xs: '', sm: '', base: '', md: 'pl-1', lg: 'pl-3', xl: 'pl-5', '2xl': 'pl-9' },
    edgeGutterRight: { none: '', '2xs': '', xs: '', sm: 'pr-1', base: 'pr-2', md: 'pr-3', lg: 'pr-5', xl: 'pr-7', '2xl': 'pr-11' },
  },
  spacious: {
    cellPadding: 'pl-4 pr-2 py-1',
    cellPaddingWithLeft: 'pl-2 pr-3 py-1',
    headerPadding: 'pl-4 pr-2 py-1.5',
    textSize: 'text-base',
    rowHeight: 56,
    headerHeight: ROW_RUNGS.tableHeader,
    cellPadLeftPx: 16,
    // Internal inset pl-4 (16px) / pr-2 (8px).
    edgeGutterLeft: { none: '', '2xs': '', xs: '', sm: '', base: '', md: '', lg: 'pl-2', xl: 'pl-4', '2xl': 'pl-8' },
    edgeGutterRight: { none: '', '2xs': '', xs: '', sm: '', base: 'pr-1', md: 'pr-2', lg: 'pr-4', xl: 'pr-6', '2xl': 'pr-10' },
  },
};

/**
 * Pixel value of each `BOX_PADDING_X` token — the Tailwind spacing scale the
 * Box padding maps emit, needed wherever table chrome does alignment math
 * against the edge inset (the grouped leading rail).
 */
export const PADDING_X_PX: Record<SpacingToken, number> = {
  none: 0, '2xs': 2, xs: 4, sm: 8, base: 12, md: 16, lg: 24, xl: 32, '2xl': 48,
};

export const CELL_ALIGNMENT_CLASS = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
} as const;

export type DataTableCellAlignment = keyof typeof CELL_ALIGNMENT_CLASS;

/**
 * Where floating row chrome seats, measured from the cell's trailing edge.
 * There is ONE float per cell — the cell's hover cluster with the row's kebab
 * adopted as its last item, or the kebab alone — so one inset serves both.
 */
export const ROW_ACTIONS_END_INSET = 4;

/**
 * Row chrome that FLOATS over the trailing edge of its cell — the identity
 * cell's action cluster, with the row's kebab as its last member — instead of
 * reserving inline width. ONE float per cell: two floats on one edge cannot
 * share a fade, since the later one's 16px lead-in lands on the earlier one's
 * last button. At rest the value under it runs the whole cell, so a name truncates
 * only where the cell ends, not 32px early for a chip nobody can see; on row
 * hover the chrome lands over the value's tail.
 *
 * It is a member of the row's hover scope (`hoverReveal="row"` supplies the
 * opacity). On a COARSE pointer it is also inert while hidden: touch has no
 * hover, so a finger's first tap on the value's tail must arm the row rather
 * than press an invisible button — pointer events return with the armed
 * row, with focus inside, and with a popup open anywhere in the row. A fine
 * pointer needs no gate (the pointer over the cell IS the row's hover, so the
 * chrome is never hidden under it) and must not have one: the first move
 * that lands on the chrome would hit an inert box, and nothing re-hits until
 * the pointer moves again, so a tooltip or a press arrives one move late.
 *
 * The chrome sits on the row's paint, not on the text: a `::before` reaches
 * 16px past its leading edge and ramps BOTH of the row's layers —
 * `--table-fill` under the alpha `--row-wash` — from transparent to solid by
 * the chrome's own edge, so the tail ghosts out under the buttons on a
 * resting, hovered or selected row alike (the same two-layer ramp as the
 * body's right-edge fade). `isolate` + `-z-10` keep the pseudo under the
 * buttons and over the text; it takes no pointer events, so the 16px lead-in
 * never intercepts a press meant for the value. No transition, like the
 * cluster it belongs to: next to actions that appear instantly an eased
 * chrome reads as arriving late.
 */
export const ROW_FLOATING_CHROME_CLASS =
  'absolute inset-y-0 isolate flex items-center transition-none ' +
  'group-has-[[data-popup-open]]/row:opacity-100 ' +
  'pointer-coarse:pointer-events-none pointer-coarse:group-hover/row:pointer-events-auto pointer-coarse:group-data-armed/row:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto pointer-coarse:group-has-[[data-popup-open]]/row:pointer-events-auto ' +
  'before:pointer-events-none before:absolute before:inset-y-0 before:right-0 before:-left-4 before:-z-10 before:bg-[linear-gradient(to_right,transparent,var(--row-wash)_16px),linear-gradient(to_right,transparent,var(--table-fill,var(--background))_16px)]';

// The press-ownership and touch-arm helpers are the library-wide ones
// (`lib/touch-arm`): a data table row arms exactly the way a `Box group`
// does, so one armed element per branch holds across tables, lists and
// cards alike.
export {
  ARMED_ATTR as ROW_ARMED_ATTR,
  PRESS_CLAIM_SELECTOR as ROW_CLICK_CLAIMED_SELECTOR,
  beginHold,
  isOwnPress,
  isTouchPress,
  pressArmed,
  pressArmedClosest,
} from "../../lib/touch-arm";

/**
 * Floor for user-resized column widths. Deliberately below the 100px auto
 * minimum — manual resize is allowed to go narrower than auto layout.
 */
export const MIN_COLUMN_WIDTH = 60;

/**
 * Default width for columns with no width metadata at all.
 */
export const DEFAULT_COLUMN_MIN_WIDTH = 100;

/**
 * Columns injected by the table itself (selection checkbox, row actions).
 * Always fixed at their meta.width: never user-resizable, never absorb fill
 * space, never stored in a sizing map.
 */
export const STRUCTURAL_COLUMN_IDS: ReadonlySet<string> = new Set([
  'select',
  '__actions__',
]);

/**
 * Resolve a column id from a column definition the same way TanStack does
 * (explicit id, then accessorKey with dots replaced, then string header).
 * Needed wherever we work with definitions before a table instance exists
 * (width resolution, persisted order defaults, the column settings panel).
 *
 * `undefined` is a backstop only. TanStack's `ColumnIdentifiers` already
 * requires either an `id` or a string `header` on every member of the
 * `ColumnDef` union, so a typed definition always resolves — the callers that
 * skip an id-less column are guarding against a definition built dynamically
 * or cast in, and the branch is unreachable from typed call sites.
 */
export function getColumnDefId(def: {
  id?: string;
  accessorKey?: unknown;
  header?: unknown;
}): string | undefined {
  return (
    def.id ??
    (typeof def.accessorKey === 'string'
      ? def.accessorKey.replace(/\./g, '_')
      : undefined) ??
    (typeof def.header === 'string' ? def.header : undefined)
  );
}

/**
 * Human-readable label for a column definition: the string header when there
 * is one, otherwise the capitalized id. Shared by the column settings panel
 * and the header-drag ghost.
 */
export function getColumnLabel(
  def: { header?: unknown },
  id: string,
): string {
  return typeof def.header === 'string' && def.header.length > 0
    ? def.header
    : id.charAt(0).toUpperCase() + id.slice(1);
}

export type ResolvableColumn = Omittable<{
  id: string;
  meta?: DataTableColumnMeta;
}>;

/**
 * A column's user-facing width bounds: the meta floor/ceiling plus the
 * container-relative cap (`maxWidthRatio`). The resize drag, the persisted-
 * sizing clamp and the default-width clamp all read THIS, so a bound holds
 * no matter which path a width arrives through. `minWidth` beats the caps
 * when they conflict (CSS clamp order); the ratio cap is inert while the
 * container is unmeasured (width 0).
 */
export function resolveColumnWidthLimits(
  col: ResolvableColumn,
  containerWidth: number,
): { min: number; max: number } {
  const min = col.meta?.minWidth ?? MIN_COLUMN_WIDTH;
  let max = col.meta?.maxWidth ?? Infinity;
  const ratio = col.meta?.maxWidthRatio;
  if (ratio !== undefined && containerWidth > 0) {
    max = Math.min(max, Math.round(containerWidth * ratio));
  }
  return { min, max: Math.max(min, max) };
}

/**
 * The width a column gets before any fill stretch or flex distribution:
 * structural meta width, else the user override, else meta
 * width/minWidth/default — non-structural results clamped to the column's
 * width limits, so a persisted out-of-bounds sizing (or a declared default
 * wider than the ratio cap allows on this container) renders back in range.
 * Shared by `resolveColumnWidths` and the fill-column base math in
 * data-table.tsx so the two can never disagree on what a column's
 * unstretched width is.
 */
export function resolveColumnBaseWidth(
  col: ResolvableColumn,
  sizing: Record<string, number>,
  containerWidth = 0,
): number {
  if (STRUCTURAL_COLUMN_IDS.has(col.id)) {
    return col.meta?.width ?? DEFAULT_COLUMN_MIN_WIDTH;
  }
  const { min, max } = resolveColumnWidthLimits(col, containerWidth);
  const base =
    sizing[col.id] ??
    col.meta?.width ??
    col.meta?.minWidth ??
    DEFAULT_COLUMN_MIN_WIDTH;
  return Math.max(Math.min(base, max), min);
}

/**
 * Resolve every visible column to a concrete pixel width — the single source
 * of truth for table layout. Rendering, sticky pinned offsets, resize-drag
 * math, and persistence all read this map, which is what makes a dragged
 * column boundary (a prefix sum of these values) track the pointer exactly.
 *
 * Pristine (no user sizing): the fill column absorbs leftover container
 * space. Without a fill column, columns lacking a fixed `meta.width` share
 * the leftover like CSS `flex: 1` (equal share from basis 0, clamped to
 * min/max; space freed by max-clamped columns redistributes; min-clamped
 * columns overflow the container rather than shrinking siblings, matching
 * Yoga).
 *
 * Customized (any user sizing): pure pixels — no auto-fill, no flex. This
 * freeze is what guarantees no column other than the dragged one ever moves
 * during or after a resize.
 */
export function resolveColumnWidths(
  columns: ResolvableColumn[],
  sizing: Record<string, number>,
  containerWidth: number,
  fillColumnId?: string,
  layout: 'fill' | 'packed' = 'fill',
): Map<string, number> {
  const widths = new Map<string, number>();
  let total = 0;
  let hasUserSizing = false;

  for (const col of columns) {
    const width = resolveColumnBaseWidth(col, sizing, containerWidth);
    if (!STRUCTURAL_COLUMN_IDS.has(col.id) && sizing[col.id] !== undefined) {
      hasUserSizing = true;
    }
    widths.set(col.id, width);
    total += width;
  }

  // Packed: every column keeps its natural width and packs left —
  // no auto-fill, no flex distribution, whatever the container measures.
  if (layout === 'packed') return widths;

  const free = containerWidth - total;
  if (hasUserSizing || free <= 0) return widths;

  if (fillColumnId !== undefined && widths.has(fillColumnId)) {
    widths.set(fillColumnId, widths.get(fillColumnId)! + free);
    return widths;
  }

  // No fill column: distribute the full (container − Σfixed) pool among
  // flexible columns from basis 0, freezing min/max violations and
  // redistributing until stable.
  const flexColumns = columns.filter(
    (col) =>
      !STRUCTURAL_COLUMN_IDS.has(col.id) && col.meta?.width === undefined,
  );
  if (flexColumns.length === 0) return widths;

  let pool = containerWidth;
  for (const col of columns) {
    if (!flexColumns.includes(col)) pool -= widths.get(col.id)!;
  }

  // The flex floor is the AUTO-layout minimum (default 100), a step above the
  // resize floor — an untouched flexible column never renders skinnier than
  // its readable default; the resolved max still honors the ratio cap.
  const flexLimits = (col: ResolvableColumn) => ({
    min: col.meta?.minWidth ?? DEFAULT_COLUMN_MIN_WIDTH,
    max: resolveColumnWidthLimits(col, containerWidth).max,
  });
  let unfrozen = flexColumns;
  while (unfrozen.length > 0) {
    const share = pool / unfrozen.length;
    const violators = unfrozen.filter((col) => {
      const { min, max } = flexLimits(col);
      return share < min || share > max;
    });
    if (violators.length === 0) {
      for (const col of unfrozen) widths.set(col.id, share);
      break;
    }
    for (const col of violators) {
      const { min, max } = flexLimits(col);
      const clamped = Math.min(Math.max(share, min), max);
      widths.set(col.id, clamped);
      pool -= clamped;
    }
    unfrozen = unfrozen.filter((col) => !violators.includes(col));
  }

  return widths;
}

/**
 * Merge a persisted column order with the table's current default order —
 * the single merge rule for the whole feature: the grid (data-table.tsx) and
 * the settings panel both derive their order from this, so the two surfaces
 * can never disagree on where a column belongs.
 *
 * Persisted ids that aren't in the current defaults are KEPT in their stored
 * slots (deduped): a column that is only transiently absent (permission or
 * variant gating) keeps its saved position when it returns. Renderers filter
 * unknown ids out at display time. Structural ids are never part of a
 * consumer order and are scrubbed from stale payloads. New default ids are
 * inserted after their nearest preceding default neighbor so a newly added
 * column lands at its intended position instead of the end.
 */
export function mergeColumnOrder(
  persisted: string[],
  defaults: string[],
): string[] {
  const merged: string[] = [];
  const present = new Set<string>();
  for (const id of persisted) {
    if (STRUCTURAL_COLUMN_IDS.has(id) || present.has(id)) continue;
    merged.push(id);
    present.add(id);
  }
  for (const [i, id] of defaults.entries()) {
    if (present.has(id)) continue;
    let insertAt = 0;
    // Nearest preceding default that already landed decides the slot.
    for (const preceding of defaults.slice(0, i).reverse()) {
      const idx = merged.indexOf(preceding);
      if (idx !== -1) {
        insertAt = idx + 1;
        break;
      }
    }
    merged.splice(insertAt, 0, id);
    present.add(id);
  }
  return merged;
}

/**
 * The show-a-column order policy, shared by every visibility editor (the
 * circle-plus picker, DataTableColumnSettings, a view's Fields page): a
 * column toggled visible lands at the END of the order rather than in its
 * stored slot — the reveal is an "add", and an add that appears mid-table is
 * easy to miss. Pass the FULL merged order so unrendered ids keep their
 * slots on the round-trip.
 */
export function columnOrderWithIdAtEnd(
  fullOrder: readonly string[],
  columnId: string,
): string[] {
  return [...fullOrder.filter((id) => id !== columnId), columnId];
}

export type ColumnMoveTarget = 'left' | 'right' | 'start' | 'end';

/**
 * Compute the drop args for a discrete column move within the reorderable
 * span, in the same (draggedId, beforeId) terms the drag-reorder drop uses.
 * Returns null when the move is a no-op (already at that edge, or the id is
 * not reorderable). `beforeId === null` means "after the last visible
 * reorderable column".
 */
export function computeColumnMove(
  columnId: string,
  reorderableIds: readonly string[],
  target: ColumnMoveTarget,
): { beforeId: string | null } | null {
  const i = reorderableIds.indexOf(columnId);
  if (i === -1) return null;
  const last = reorderableIds.length - 1;
  // An index past the span reads as "after the last reorderable column", which
  // is the same value `beforeId` already uses for that position.
  const at = (index: number): string | null => reorderableIds[index] ?? null;
  switch (target) {
    case 'left':
      return i <= 0 ? null : { beforeId: at(i - 1) };
    case 'right':
      // The drop removes the moved id before inserting, so landing one slot
      // right means inserting before the element two ahead; when the right
      // neighbor is last, "right" collapses to "end".
      if (i >= last) return null;
      return { beforeId: i + 2 <= last ? at(i + 2) : null };
    case 'start':
      return i <= 0 ? null : { beforeId: at(0) };
    case 'end':
      return i >= last ? null : { beforeId: null };
  }
}

// ---------------------------------------------------------------------------
// CSS custom properties for column widths
// ---------------------------------------------------------------------------
//
// Widths render through per-column CSS variables set on the scroll container.
// Cell/header style objects reference the variables and stay referentially
// stable, so a resize drag updates ONE variable per frame imperatively and
// never re-renders rows. React state commits once on pointer-up.

function cssSafeId(id: string): string {
  return id.replace(/[^a-zA-Z0-9_-]/g, '_');
}

export function columnWidthVar(id: string): string {
  return `--dt-w-${cssSafeId(id)}`;
}

export function columnLeftVar(id: string): string {
  return `--dt-l-${cssSafeId(id)}`;
}

export function columnRightVar(id: string): string {
  return `--dt-r-${cssSafeId(id)}`;
}

export const TABLE_TOTAL_WIDTH_VAR = '--dt-total';

/**
 * Get truncation class based on meta.
 */
export function getTruncationClass(meta?: DataTableColumnMeta): string {
  if (meta?.truncate === false) {
    return '';
  }
  return 'overflow-hidden';
}

/**
 * Selection column width (fixed).
 */
export const SELECTION_COLUMN_WIDTH = 44;

/**
 * Actions column width (fixed).
 */
export const ACTIONS_COLUMN_WIDTH = 60;

/**
 * Extract column meta with proper typing.
 */
export function getColumnMeta(
  column: { columnDef: { meta?: unknown } }
): DataTableColumnMeta | undefined {
  return column.columnDef.meta as DataTableColumnMeta | undefined;
}

/**
 * Default group key for ungrouped tables (single group containing all rows).
 */
export const ALL_GROUP_KEY = '__all__';

/**
 * Group key for items that have no assignment (e.g. no team, no role).
 */
export const UNASSIGNED_GROUP_KEY = '__unassigned__';

/**
 * How many items from the end of loaded data to trigger loadMore.
 */
export const NEAR_END_THRESHOLD = 10;
